import { Injectable } from '@angular/core';
import { ToastMessageOptions } from 'primeng/api';

export interface TicketActivityMeta {
  title: string;
  icon: string;
}

export interface TicketChangeEntry {
  summary: string;
  detail: string;
}

/**
 * Dados anexados ao ÚNICO toast de alteração direta do chamado pendente por vez
 * (`data` do `ToastMessageOptions`). `resolved` evita executar `onAction`/`onReject` duas
 * vezes (ex.: clique no botão seguido do fechamento/expiração natural do toast).
 *
 * Esse toast compartilha a MESMA chave/pilha (`ticket-activity`) dos toasts informativos de
 * atividade (comentário/apontamento/anexo/tarefa) de propósito — formam um histórico único,
 * mais recente sempre no topo, em vez de duas pilhas sem relação visual entre si. Isso exige que
 * qualquer fechamento seja feito por mensagem específica (nunca `messageService.clear(key)`, que
 * fecharia TUDO na pilha, inclusive avisos informativos não relacionados). `id` existe só para
 * isso: junto com `ticket-changed-id-{id}` no `styleClass`, permite localizar o botão "X" nativo
 * exatamente desse toast no DOM — ver `resolveTicketChangeAt` nos componentes.
 */
export interface TicketChangedToastData {
  id: number;
  resolved: boolean;
  /** Uma entrada por notificação de alteração direta recebida enquanto o toast está pendente. */
  changes: TicketChangeEntry[];
  onAction: () => void;
  onReject: () => void;
}

/**
 * Interpreta o payload do canal Reverb `ticket-{uuid}` e monta o toast personalizado
 * usado para alterações indiretas (comentário, apontamento, anexo, tarefa), evitando
 * a divergência que existia entre ticket-detail.component.ts e ticket-detail-modal.component.ts.
 */
@Injectable({ providedIn: 'root' })
export class TicketChannelNotificationService {
  private static activityAudioCtx?: AudioContext;
  private static ticketChangedIdSeq = 0;

  /** Identificador único e crescente para escopar o fechamento de um toast de alteração
   * direta específico no DOM (ver `TicketChangedToastData`). */
  static nextTicketChangedId(): number {
    return ++this.ticketChangedIdSeq;
  }

  /** Ausente/`'ticket'` = alteração direta de campo do chamado; qualquer outro valor = atividade indireta. */
  static isDirectTicketChange(changeType: string | null | undefined): boolean {
    return (changeType ?? 'ticket') === 'ticket';
  }

  static getActivityMeta(changeType: string): TicketActivityMeta {
    switch (changeType) {
      case 'ticket_logs':
        return { title: 'Novo apontamento', icon: 'pi pi-clock' };
      case 'ticket_details':
        return { title: 'Novo comentário', icon: 'pi pi-comment' };
      case 'ticket_documents':
        return { title: 'Novo anexo', icon: 'pi pi-paperclip' };
      case 'ticket_tasks':
        return { title: 'Tarefa atualizada', icon: 'pi pi-check-square' };
      default:
        return { title: 'Atualização do chamado', icon: 'pi pi-bell' };
    }
  }

  /** Monta o objeto pronto para `messageService.add(...)` (toast custom `key: 'ticket-activity'`). */
  static buildActivityToastMessage(
    changeType: string,
    user: string,
    changes: string
  ): ToastMessageOptions {
    const meta = this.getActivityMeta(changeType);
    return {
      key: 'ticket-activity',
      severity: 'info',
      icon: meta.icon,
      summary: user ? `${meta.title} · ${user}` : meta.title,
      detail: changes,
      // Tempo maior que o padrão do sistema — dá tempo de ler o comentário/apontamento;
      // o toast também tem botão de fechar para dispensar antes disso.
      life: 15000,
    };
  }

  static buildTicketChangeEntry(user: string, changes: string): TicketChangeEntry {
    return {
      summary: user ? `Chamado atualizado · ${user}` : 'Chamado atualizado',
      detail: changes,
    };
  }

  /**
   * Monta o toast de alteração DIRETA de campos do chamado (substitui o antigo modal
   * bloqueante Sim/Não). `data.onAction`/`data.onReject` equivalem ao antigo "Sim"/"Não".
   * Quando `data.changes` tem mais de uma entrada (várias alterações chegaram antes de o
   * usuário decidir), o título vira "N alterações no chamado" e o detalhe lista todas juntas.
   */
  static buildTicketChangedToastMessage(data: TicketChangedToastData): ToastMessageOptions {
    const count = data.changes.length;
    const latest = data.changes[count - 1];
    const summary = count > 1 ? `${count} alterações no chamado` : latest.summary;
    const detail = data.changes.map(entry => entry.detail).filter(Boolean).join('\n');
    return {
      // Mesma chave/pilha dos toasts informativos de propósito — ver `TicketChangedToastData`.
      key: 'ticket-activity',
      severity: 'warn',
      icon: 'pi pi-sync',
      // A classe `ticket-changed-id-{id}` não tem CSS próprio — é só um gancho para localizar
      // esse toast específico no DOM (ver `TicketChangedToastData`/`resolveTicketChangeAt`).
      styleClass: `ticket-activity-toast--action ticket-changed-id-${data.id}`,
      summary,
      detail,
      // Mais tempo que o toast de atividade comum — é uma decisão, não só um aviso informativo.
      life: 20000,
      data,
    };
  }

  /**
   * Executa `onAction`/`onReject` uma única vez por toast (`resolved` faz o segundo
   * disparo — vindo do fechamento manual, do atalho de teclado ou da expiração — virar no-op).
   */
  static resolveTicketChanged(
    data: TicketChangedToastData | null | undefined,
    viaAction: boolean
  ): void {
    if (!data || data.resolved) return;
    data.resolved = true;
    if (viaAction) {
      data.onAction();
    } else {
      data.onReject();
    }
  }

  /**
   * Atalho de teclado para "atualizar" o toast de alteração direta, sem depender de clique.
   * `Ctrl+.` — verificado como não reservado em Chrome/Firefox/Safari/Edge (Windows/Linux/macOS)
   * e fora do conjunto de bindings estilo Emacs do Control no macOS (que são baseados em letras:
   * A/B/D/E/F/K/N/P/T/Y...), então não move cursor nem apaga texto em campos nativos. Combinações
   * com Ctrl nunca inserem caractere em inputs, então é seguro mesmo com um campo em foco.
   */
  static isUpdateShortcut(event: KeyboardEvent): boolean {
    return event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey && event.key === '.';
  }

  /** Som curto (Web Audio) para a notificação de atividade — best-effort, sem asset. */
  static playActivitySound(): void {
    try {
      const Ctx =
        (
          window as unknown as {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }
        ).AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      if (!this.activityAudioCtx) this.activityAudioCtx = new Ctx();
      const ctx = this.activityAudioCtx;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      const notes = [
        { f: 880, t: 0 }, // A5
        { f: 1174.66, t: 0.11 }, // D6
      ];
      for (const n of notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = n.f;
        gain.gain.setValueAtTime(0.0001, now + n.t);
        gain.gain.exponentialRampToValueAtTime(0.14, now + n.t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + n.t + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + n.t);
        osc.stop(now + n.t + 0.2);
      }
    } catch {
      // Som é opcional; ignora se o browser bloquear (ex.: sem interação prévia).
    }
  }
}
